import { FC, PropsWithChildren, SyntheticEvent, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Slider } from "@mui/material";

import { SearchTextField } from "@ui/index";
import { Select } from "@ui/interactive/Select";

import { CatalogLocalStorage } from "@pages/Catalog";
import { CatalogTab, ECatalogue } from "@pages/Catalog/components/utils";
import { catalogTabs } from "@pages/Catalog/constants";
import { CatalogPageContext } from "@pages/Catalog/context";

import { useFilters } from "@hooks/useFiltersQuery";

import { useAppSelector } from "@store/hooks";
import { getQuickFiltersSelector } from "@store/selectors/quickFilters";

import { PlayerContext, TPlayerContext } from "@layouts/PlayerLayout/utils";
import styled, { keyframes } from "styled-components";

import {
  getIsMuted,
  getVolume,
  setMuted as persistMuted,
  setVolume as persistVolume,
} from "@/components/BottomPlayer/utils/helpers/volumeLocalStorage";
import { AccountType, useAccountType } from "@/contexts/AccountTypeContext";
import CatalogueSkeleton from "@/pages/Catalog/components/CatalogueSkeleton";
import FiltersSection from "@/pages/Catalog/components/Filters/FiltersSection";
import { getAccumulatedTracks } from "@/store/selectors/getCatalogueSelector";
import { ERoutes, visibleSortValues } from "@/utils/constants";

import { CategoryButtons } from "../CategoryButtons";

// -- Styled components for new layout --

const PageLayout = styled.div<{ $withSidebar: boolean }>`
  display: grid;
  grid-template-columns: ${({ $withSidebar }) => ($withSidebar ? "296px 1fr" : "1fr")};
  min-height: calc(100vh - 122px);
  transition: grid-template-columns 0.3s ease;
`;

const Sidebar = styled.div`
  background: rgba(75, 55, 39, 0.25);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  padding: 24px 24px 20px 0;
  min-height: 100%;
  overflow-y: auto;
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0;
  min-height: 40px;
  font-family: "Raleway", sans-serif;
  font-size: 1rem;
  font-weight: 400;
  color: #fbf4ec;
  cursor: pointer;
  letter-spacing: 1px;
  margin-bottom: 16px;
`;

const CollapseButton = styled.span`
  margin-left: auto;
  color: #ac8d75;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 6px;

  &:hover {
    color: #ffdca8;
    background: rgba(255, 255, 255, 0.1);
  }

  svg {
    width: 20px;
    height: 20px;
    stroke: currentColor;
    fill: none;
    stroke-width: 1.5;
  }
`;

const PageContent = styled.div`
  padding: 48px 0 160px;
`;



const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;

  /* Override search input to match demo dark style */
  .MuiOutlinedInput-root {
    background: rgba(255, 255, 255, 0.06) !important;
    border: 1px solid rgba(186, 144, 92, 0.2) !important;
    border-radius: 8px;

    &.Mui-focused {
      border-color: #ba905c !important;
    }
  }

  .MuiOutlinedInput-input {
    color: #fbf4ec !important;

    &::placeholder {
      color: rgba(251, 244, 236, 0.5) !important;
    }
  }

  .MuiOutlinedInput-notchedOutline {
    border: none !important;
  }
`;


const ContentPanel = styled.div`
  background: rgba(83, 57, 36, 0.68);
  border: 1px solid rgba(186, 144, 92, 0.08);
  border-radius: 12px;
  overflow: hidden;
`;

const PillTabsContainer = styled.div`
  display: flex;
  position: relative;
  margin-right: 6px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(186, 144, 92, 0.2);
  border-radius: 8px;
  padding: 3px;
  gap: 4px;
`;

const TabUnderline = styled.div<{ $left: number; $width: number }>`
  display: none;
`;

const PillTab = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "Raleway", sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  border-radius: 6px;
  padding: 0 20px;
  height: 36px;
  color: #FBF4EC;
  background: ${({ $active }) => ($active ? "rgba(255, 220, 168, 0.15)" : "transparent")};

  &:hover {
    color: #fbf4ec;
    background: ${({ $active }) => ($active ? "rgba(255, 220, 168, 0.15)" : "rgba(255, 255, 255, 0.06)")};
  }

  svg {
    width: 16px;
    height: 16px;

    path {
      fill: ${({ $active }) => ($active ? "#9cd3fd" : "#e0d2c1")};
      transition: fill 0.2s ease;
    }

    ${({ $active }) =>
      $active &&
      `
      filter: drop-shadow(0 0 4px rgba(156, 211, 253, 0.3));
    `}
  }
`;

const FreeUseLink = styled.span`
  font-family: "Raleway", sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  letter-spacing: 0.02em;
  color: #ffffff;
  margin-left: auto;
  margin-right: 0;
  display: inline-flex;
  align-items: center;
  height: 44px;
  padding: 0 16px;
  border: 1px solid rgba(186, 144, 92, 0.2);
  border-radius: 8px;
  flex-shrink: 0;
  white-space: nowrap;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.06);
  box-sizing: border-box;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    color: #ffdca8;
  }
`;

const ResultsBar = styled.div<{ $searchActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 20px 16px;
  border-bottom: 1px solid rgba(186, 144, 92, 0.15);

  /* Search input styling */
  .MuiOutlinedInput-root {
    background: rgba(255, 255, 255, 0.06) !important;
    border: 1px solid ${({ $searchActive }) => $searchActive ? "rgba(255, 210, 155, 0.6)" : "rgba(186, 144, 92, 0.2)"} !important;
    border-radius: 8px;
    transition: border-color 0.2s ease;

    &.Mui-focused {
      border-color: ${({ $searchActive }) => $searchActive ? "rgba(255, 210, 155, 0.6)" : "rgba(186, 144, 92, 0.2)"} !important;
    }
  }

  .MuiOutlinedInput-input {
    color: #fbf4ec !important;

    &::placeholder {
      color: rgba(251, 244, 236, 0.5) !important;
    }
  }

  .MuiOutlinedInput-notchedOutline {
    border: none !important;
  }
`;

const searchNoticeFadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const SearchNotice = styled.div`
  margin: 14px 16px 0;
  padding: 12px 16px;
  border: 1px solid rgba(235, 194, 142, 0.35);
  background: rgba(235, 194, 142, 0.08);
  border-radius: 10px;
  color: #f5dfc4;
  font-family: "Raleway", sans-serif;
  font-size: 0.9rem;
  line-height: 1.45;
  animation: ${searchNoticeFadeIn} 0.4s ease both;
`;

const VolumeWrapper = styled.div<{ $muted?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-left: 6px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(186, 144, 92, 0.2);
  border-radius: 8px;
  padding: 0 14px;
  height: 46px;
  .volume-icon {
    width: 18px;
    height: 18px;
    cursor: pointer;
    color: #e0d2c1;
    flex-shrink: 0;

    svg {
      width: 100%;
      height: 100%;
    }
  }

  .MuiSlider-root {
    width: 100px;
    height: 2px;
    padding: 6px 0;
    color: #e0d2c1;
    transition: opacity 0.2s ease;
    opacity: ${({ $muted }) => ($muted ? 0.5 : 1)};
  }
  .MuiSlider-track {
    background: #e0d2c1;
    border: none;
    height: 2px;
  }
  .MuiSlider-rail {
    height: 2px;
    background: rgba(255, 255, 255, 0.25);
  }
  .MuiSlider-thumb {
    width: 12px;
    height: 12px;
    background: #e0d2c1;
    box-shadow: none;
    &:hover, &.Mui-focusVisible {
      box-shadow: 0 0 8px rgba(224, 210, 193, 0.4);
    }
  }
`;

const SortWrapper = styled.div`
  flex-shrink: 0;
  margin-left: 4px;

  .MuiInputBase-root {
    min-width: 0 !important;
    width: auto !important;
    background: rgba(255, 255, 255, 0.06) !important;
    border: 1px solid rgba(186, 144, 92, 0.2) !important;
    border-radius: 8px !important;
    height: 46px !important;
    cursor: pointer !important;
  }

  .MuiSelect-select {
    background: transparent !important;
    border: none !important;
    color: #e0d2c1 !important;
    font-family: "Raleway", sans-serif !important;
    font-size: 0.875rem !important;
    letter-spacing: 0.02em !important;
    padding: 0 32px 0 14px !important;
    min-height: 0 !important;
    width: auto !important;
    transform: translateY(1px);
  }

  .MuiOutlinedInput-notchedOutline {
    border: none !important;
  }

  .Mui-focused .MuiOutlinedInput-notchedOutline {
    border: none !important;
  }

  .Mui-focused.MuiInputBase-root,
  .MuiInputBase-root.Mui-focused {
    border-color: rgba(186, 144, 92, 0.2) !important;
    box-shadow: none !important;
  }

  /* Custom chevron matching demo */
  .MuiSelect-icon {
    display: none !important;
  }

  .MuiSelect-select::after {
    content: "";
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    width: 10px;
    height: 6px;
    background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23AC8D75' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
  }

  /* Auto-width for select stack */
  > * {
    min-width: 0 !important;
    width: auto !important;
  }
`;

const TracksArea = styled.div`
  padding: 14px 16px 16px;
`;

// -- Component --

type TMainContentDesktop = {
  isSearchLoading?: boolean;
  isCatalogueLoading: boolean;
  isToggleBetween: React.MutableRefObject<boolean>;
  catalogRef: React.MutableRefObject<HTMLElement>;
  searchAnchorEl?: HTMLElement;
  setSearchAnchorEl?: React.Dispatch<React.SetStateAction<HTMLElement>>;
  smartResults?: any[];
} & PropsWithChildren;

export const MainContentDesktop: FC<TMainContentDesktop> = ({
  isSearchLoading,
  isToggleBetween,
  catalogRef,
  isCatalogueLoading,
  searchAnchorEl: _searchAnchorEl,
  setSearchAnchorEl: _setSearchAnchorEl,
  children,
  smartResults: _smartResults,
}) => {
  const { t, state, dispatchReducer, handlers, isSmartSearch, noResultsQuery } = useContext(CatalogPageContext);
  const { handleToggleSwitch, handleSetFilters, handleToggleModal, handleSearchChange, handleSearchSubmit } = handlers;
  // Entitlements, not plan identity: an account awaiting reactivation is on
  // the subscription plan but is not paying for it, so it browses and
  // downloads on free terms until it reactivates (ToU 17.4).
  const { entitlementType: accountType } = useAccountType();
  const isSub = accountType === AccountType.SUBSCRIPTION;
  const isMusicCreator = accountType === AccountType.MUSIC_CREATOR;
  const useTestLabel = isSub ? "Subscription or License? (Test)" : "Free, Subscription or License? (Test)";
  const searchValue = useContext(CatalogPageContext)?.localSearch ?? "";
  const { isLoading: isQuickFilterLoading } = useAppSelector(getQuickFiltersSelector);
  const location = useLocation();
  const { handleSetQuery, query } = useFilters();
  const navigate = useNavigate();

  const accumulatedTracks = useAppSelector(getAccumulatedTracks);
  const hasExistingTracks = accumulatedTracks.length > 0;

  // Volume control — synced with bottom player via localStorage + media element
  const { media } = useContext(PlayerContext) as TPlayerContext;
  const [volume, setVolumeState] = useState(getVolume);
  const [isMuted, setIsMutedState] = useState(getIsMuted);
  const volumeBeforeMuteRef = useRef(getVolume() || 1);

  const applyVolume = (vol: number, muted: boolean) => {
    setVolumeState(vol);
    persistVolume(vol);
    setIsMutedState(muted);
    persistMuted(muted);
    if (media) {
      media.volume = vol;
      media.muted = muted;
    }
    window.dispatchEvent(new CustomEvent("as:volume-sync", { detail: { volume: vol, muted } }));
  };

  const handleVolumeChange = (_e: Event | SyntheticEvent, value: number | number[]) => {
    if (Array.isArray(value)) return;
    if (value > 0) volumeBeforeMuteRef.current = value;
    applyVolume(value, !value);
  };

  const handleMuteToggle = () => {
    if (isMuted || volume === 0) {
      const restoreVol = volumeBeforeMuteRef.current || 1;
      applyVolume(restoreVol, false);
    } else {
      volumeBeforeMuteRef.current = volume;
      applyVolume(0, true);
    }
  };

  // Listen for volume changes from bottom player
  useEffect(() => {
    const handleSync = (e: Event) => {
      const { volume: vol, muted } = (e as CustomEvent).detail;
      setVolumeState(vol);
      setIsMutedState(muted);
      if (vol > 0) volumeBeforeMuteRef.current = vol;
    };
    window.addEventListener("as:volume-sync", handleSync);
    return () => window.removeEventListener("as:volume-sync", handleSync);
  }, []);


  // Animated tab underline
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [underline, setUnderline] = useState({ left: 0, width: 0 });

  const updateUnderline = useCallback(() => {
    const activeTab = tabRefs.current.get(state.catalogTab);
    const container = tabsContainerRef.current;
    if (activeTab && container) {
      const containerRect = container.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();
      setUnderline({
        left: tabRect.left - containerRect.left,
        width: tabRect.width,
      });
    }
  }, [state.catalogTab]);

  useLayoutEffect(() => {
    updateUnderline();
  }, [state.catalogTab, updateUnderline]);

  useEffect(() => {
    window.addEventListener("resize", updateUnderline);
    return () => window.removeEventListener("resize", updateUnderline);
  }, [updateUnderline]);


  useEffect(() => {
    if (state.catalogTab === CatalogTab.QUICK_FILTERS || location.pathname.includes(ERoutes.QuickFilter)) {
      handleSetQuery({ isFiltersOpen: false });
    }
  }, [state.catalogTab]);

  useEffect(() => {
    dispatchReducer({
      type: ECatalogue.OpenFilters,
      payload: query.isFiltersOpen,
    });
    localStorage.setItem(CatalogLocalStorage.FILTERS_SECTION, String(query.isFiltersOpen));
  }, [query.isFiltersOpen]);

  const navigateToQuickFilter = () => {
    if (location.pathname.includes(ERoutes.QuickFilter)) {
      navigate(ERoutes.Catalog);
    }
  };

  const openQuestionDialog = () => {
    dispatchReducer({
      type: ECatalogue.OPEN_QUESTION_FLOW,
      payload: true,
    });
  };

  return (
    <PageLayout $withSidebar={false} ref={catalogRef as React.RefObject<HTMLDivElement>}>
      {/* SIDEBAR - disabled */}
      {false && (
        <Sidebar>
          <SidebarHeader onClick={handleToggleModal}>
            <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, stroke: "currentColor", fill: "none", strokeWidth: 1.5 }}>
              <path d="M4 21V14M4 10V3M12 21V12M12 8V3M20 21V16M20 12V3M1 14h6M9 8h6M17 16h6" />
            </svg>
            <span>Filters</span>
            <CollapseButton>
              <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="4" x2="5" y2="20" />
                <path d="M19 12H9m4-4l-4 4 4 4" />
              </svg>
            </CollapseButton>
          </SidebarHeader>
          <FiltersSection handleSetFilters={handleSetFilters} />
        </Sidebar>
      )}

      {/* CONTENT AREA */}
      <PageContent>
        {/* TOOLBAR: title + free use alert */}
        <Toolbar>
          <span style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.5rem", fontWeight: 500, color: "#fbf4ec", paddingLeft: "8px", marginRight: "12px" }}>Browse Music</span>
          <PillTabsContainer ref={tabsContainerRef} style={{ flexShrink: 0, marginRight: "auto" }}>
            {catalogTabs.map(({ value, label, icon }) => (
              <PillTab
                key={value}
                ref={(el) => { if (el) tabRefs.current.set(value, el); }}
                $active={state.catalogTab === value}
                onClick={() => {
                  if (value === CatalogTab.QUICK_FILTERS) navigateToQuickFilter();
                  isToggleBetween.current = true;
                  handleToggleSwitch(value);
                }}
              >
                {icon}
                {t(`${label}.desktop`)}
              </PillTab>
            ))}
            <TabUnderline $left={underline.left} $width={underline.width} />
          </PillTabsContainer>
          {!isMusicCreator && (
            <FreeUseLink onClick={openQuestionDialog}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 7, flexShrink: 0, transform: "translateY(-1px)" }}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
              {useTestLabel}
            </FreeUseLink>
          )}
        </Toolbar>

        {/* CONTENT PANEL */}
        <ContentPanel>
          {/* TABS + SEARCH + VOLUME + SORT */}
          <ResultsBar $searchActive={isSmartSearch}>
            <SearchTextField
              value={searchValue}
              handleChange={handleSearchChange}
              placeholder={t("searchPlaceholder") ?? ""}
              variant="dark"
              loading={!!isSearchLoading}
              iconOffsetY={1}
              onKeyDown={(e) => { if (e.key === "Enter") handleSearchSubmit(); }}
              sx={{
                flex: 1,
                "& .MuiInputBase-root": { fontFamily: "Raleway, sans-serif !important", letterSpacing: "0.02em !important", padding: "12px 14px !important", minHeight: "46px !important", boxSizing: "border-box" },
                "& .MuiInputBase-input": { fontFamily: "Raleway, sans-serif !important", letterSpacing: "0.02em !important", padding: "0 !important" },
                "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 8px rgba(186, 144, 92, 0.1)" },
                minWidth: "280px",
                margin: 0,
              }}
            />

            <SortWrapper>
              <Select
                data={visibleSortValues(t)}
                handleSelect={(selected) => {
                  dispatchReducer({
                    type: ECatalogue.SortBy,
                    payload: selected,
                  });
                }}
                value={state.sortBy.value}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "#6b4e37 !important",
                      border: "1px solid rgba(235, 194, 142, 0.28) !important",
                      borderRadius: "10px !important",
                      padding: "6px !important",
                      boxShadow: "0 12px 32px rgba(0,0,0,0.35) !important",
                      marginTop: "6px !important",
                      ".MuiMenuItem-root": {
                        color: "#e0d2c1 !important",
                        fontFamily: "Raleway !important",
                        fontSize: "0.875rem !important",
                        borderRadius: "6px !important",
                        "&:hover": {
                          backgroundColor: "#5c412a !important",
                          color: "#ffffff !important",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "transparent !important",
                          color: "#ffffff !important",
                          fontWeight: "500 !important",
                        },
                      },
                    },
                  },
                }}
              />
            </SortWrapper>

            <VolumeWrapper $muted={isMuted}>
              <span className="volume-icon" onClick={handleMuteToggle}>
                {isMuted ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  </svg>
                )}
              </span>
              <Slider
                value={isMuted ? 0 : volume}
                min={0}
                max={1}
                step={0.05}
                onChange={handleVolumeChange}
              />
            </VolumeWrapper>
          </ResultsBar>

          {noResultsQuery && (
            <SearchNotice>
              No results for your search. Below are some other tracks for you to explore.
            </SearchNotice>
          )}

          {/* TRACKS / ALBUMS / QUICK FILTERS */}
          <TracksArea>
            {state.catalogTab === CatalogTab.QUICK_FILTERS &&
            !location.pathname.includes(ERoutes.QuickFilter) ? (
              <>
                <CategoryButtons />
                {isQuickFilterLoading || isCatalogueLoading ? (
                  <CatalogueSkeleton
                    isCollapsed={state.isFiltersOpen}
                    isFilter={state.isFiltersOpen && !!state.filters.length}
                  />
                ) : (
                  children
                )}
              </>
            ) : (isQuickFilterLoading || isCatalogueLoading) && !hasExistingTracks ? (
              <CatalogueSkeleton
                isCollapsed={state.isFiltersOpen}
                isFilter={state.isFiltersOpen && !!state.filters.length}
              />
            ) : (
              children
            )}
          </TracksArea>
        </ContentPanel>
      </PageContent>
    </PageLayout>
  );
};
